import { convertDateTimeToNet } from "../../../../utils/datetimeUtils";
import { formatDistance } from "../../../../utils/googleMapsUtils";

export const catalogOptionLabel = (option) =>
  option?.nombre ?? option?.name ?? "";

export const toFormikSelectValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

export const emptySelectValue = (value) =>
  value === null || value === undefined ? "" : value;

export const normalizeTipoDireccion = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
};

export const toFormikString = (value) =>
  value === null || value === undefined ? "" : String(value);

export const toFormikDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const resolveFormDate = (source, ...keys) => {
  for (const key of keys) {
    const raw = source?.[key];
    if (raw === null || raw === undefined || raw === "") continue;
    return toFormikDate(raw);
  }
  return null;
};

export const resolveFormValue = (source, ...keys) => {
  for (const key of keys) {
    const raw = source?.[key];
    if (raw !== null && raw !== undefined && raw !== "") return raw;
  }
  return "";
};

const coerceToArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

export const getFormFieldOptions = (fields, ...keys) => {
  if (!fields) return [];
  for (const key of keys) {
    const options = fields[key]?.options;
    if (options?.length) return options;
  }
  return [];
};

export const ORIGENES_PEDIDO = [
  { id: "Prestador", nombre: "Prestador" },
  { id: "CRM", nombre: "CRM" },
  { id: "Mail", nombre: "Mail" },
  { id: "Gestor", nombre: "Gestor" },
];

export const TIPOS_DIRECCION = [
  { id: 1, nombre: "Accidentado" },
  { id: 2, nombre: "Comisión Médica" },
  { id: 3, nombre: "Empleador" },
  { id: 4, nombre: "Prestador Médico" },
  { id: 5, nombre: "Otros" },
];

export const LEGACY_POINT_FIELDS = [
  "calle",
  "numero",
  "piso",
  "departamento",
  "entreCalle",
  "entreCalle1",
  "localidad",
  "provincia",
  "codigoPostal",
  "telefonos",
];

const createPuntoId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const createEmptyTramoGestion = (overrides = {}) => ({
  idTipoTransporte: "",
  idMotivoTraslado: 0,
  idPrestadorTraslado: "",
  idAgencia: 0,
  pool: false,
  acompanante: false,
  sillaRuedas: false,
  muletas: false,
  observacionesTramo: "",
  ...overrides,
});

export const copyTramoGestion = (source = {}) =>
  createEmptyTramoGestion({
    idTipoTransporte: emptySelectValue(source.idTipoTransporte),
    idMotivoTraslado:
      source.idMotivoTraslado === "" || source.idMotivoTraslado == null
        ? 0
        : source.idMotivoTraslado,
    idPrestadorTraslado: emptySelectValue(source.idPrestadorTraslado),
    idAgencia:
      source.idAgencia === "" || source.idAgencia == null
        ? 0
        : source.idAgencia,
    pool: Boolean(source.pool),
    acompanante: Boolean(source.acompanante),
    sillaRuedas: Boolean(source.sillaRuedas),
    muletas: Boolean(source.muletas),
    observacionesTramo: source.observacionesTramo || "",
  });

export const applyTramoGestionField = (punto, defaults, fieldKey) => {
  if (fieldKey === "requerimientosEspeciales") {
    return {
      ...punto,
      pool: Boolean(defaults.pool),
      acompanante: Boolean(defaults.acompanante),
      sillaRuedas: Boolean(defaults.sillaRuedas),
      muletas: Boolean(defaults.muletas),
    };
  }
  if (fieldKey === "idAgencia") {
    return {
      ...punto,
      idAgencia: defaults.idAgencia,
      ...(defaults.idPrestadorTraslado
        ? { idPrestadorTraslado: defaults.idPrestadorTraslado }
        : {}),
    };
  }
  return {
    ...punto,
    [fieldKey]: defaults[fieldKey],
  };
};

export const applyDefaultsFieldToPuntos = (
  puntos,
  tramosDefaults,
  fieldKey,
  idEstadoInicial,
) => {
  let changed = false;
  const next = (puntos || []).map((punto, index) => {
    if (index === 0 || !isTramoEditable(punto, idEstadoInicial)) return punto;
    const updated = applyTramoGestionField(punto, tramosDefaults, fieldKey);
    if (fieldKey === "requerimientosEspeciales") {
      if (
        punto.pool === updated.pool &&
        punto.acompanante === updated.acompanante &&
        punto.sillaRuedas === updated.sillaRuedas &&
        punto.muletas === updated.muletas
      ) {
        return punto;
      }
    } else if (fieldKey === "idAgencia") {
      if (
        punto.idAgencia === updated.idAgencia &&
        punto.idPrestadorTraslado === updated.idPrestadorTraslado
      ) {
        return punto;
      }
    } else if (punto[fieldKey] === updated[fieldKey]) {
      return punto;
    }
    changed = true;
    return updated;
  });
  return changed ? next : puntos;
};

export const createEmptyPunto = (overrides = {}) => ({
  id: createPuntoId(),
  tipoDireccion: "",
  fechaHora: null,
  observaciones: "",
  ...createEmptyTramoGestion(),
  direccionHabitual: null,
  calle: "",
  numero: "",
  piso: "",
  departamento: "",
  entreCalle: "",
  entreCalle1: "",
  localidad: "Capital Federal",
  provincia: "Capital Federal",
  codigoPostal: "",
  telefonos: "",
  latitud: "",
  longitud: "",
  formattedAddress: "",
  placeId: "",
  location: null,
  distanceMeters: null,
  distanciaLabel: "",
  esRetorno: false,
  recorridoEditable: true,
  gestionEditable: true,
  ...overrides,
});

export const normalizeTramoGestion = (source = {}) =>
  createEmptyTramoGestion({
    idTipoTransporte: emptySelectValue(source.idTipoTransporte),
    idMotivoTraslado:
      source.idMotivoTraslado === "" || source.idMotivoTraslado == null
        ? 0
        : source.idMotivoTraslado,
    idPrestadorTraslado: emptySelectValue(source.idPrestadorTraslado),
    idAgencia:
      source.idAgencia === "" || source.idAgencia == null
        ? 0
        : source.idAgencia,
    pool: Boolean(source.pool),
    acompanante: Boolean(source.acompanante),
    sillaRuedas: Boolean(source.sillaRuedas),
    muletas: Boolean(source.muletas),
    observacionesTramo: toFormikString(source.observacionesTramo),
  });

export const normalizePunto = (punto = {}) => {
  const base = createEmptyPunto(punto);
  return {
    ...base,
    ...normalizeTramoGestion(punto),
    tipoDireccion: normalizeTipoDireccion(punto.tipoDireccion),
    fechaHora: toFormikDate(punto.fechaHora ?? punto.fechaHoraTraslado),
    observaciones: toFormikString(punto.observaciones),
    calle: toFormikString(punto.calle),
    numero: toFormikString(punto.numero),
    piso: toFormikString(punto.piso),
    departamento: toFormikString(punto.departamento),
    entreCalle: toFormikString(punto.entreCalle),
    entreCalle1: toFormikString(punto.entreCalle1),
    localidad: toFormikString(punto.localidad || base.localidad),
    provincia: toFormikString(punto.provincia || base.provincia),
    codigoPostal: toFormikString(punto.codigoPostal),
    telefonos: toFormikString(punto.telefonos),
    latitud: toFormikString(punto.latitud),
    longitud: toFormikString(punto.longitud),
    formattedAddress: toFormikString(punto.formattedAddress),
    placeId: toFormikString(punto.placeId),
    distanciaLabel: toFormikString(punto.distanciaLabel ?? base.distanciaLabel),
  };
};

export const createInitialPuntos = () => [
  createEmptyPunto({ distanceMeters: 0, distanciaLabel: "0 km" }),
  createEmptyPunto(),
];

export const isPuntoRetornoAuto = (punto) => Boolean(punto?.esRetorno);

export const getPuntoHeaderTitle = (index, total, punto) => {
  if (index === 0) return "Punto de origen";
  if (isPuntoRetornoAuto(punto)) return "Retorno al origen";
  if (index === total - 1) return "Punto final";
  return `Parada intermedia ${index}`;
};

export const ensureMinPuntos = (puntos) => {
  const next = puntos?.length ? [...puntos] : [];
  while (next.length < 2) {
    next.push(
      createEmptyPunto(
        next.length === 0 ? { distanceMeters: 0, distanciaLabel: "0 km" } : {},
      ),
    );
  }
  if (next[0].distanceMeters == null) {
    next[0] = { ...next[0], distanceMeters: 0 };
  }
  return next;
};

export const puntoToDireccionValues = (punto = {}) => ({
  direccionHabitualDesde: null,
  tipoDireccionDesde: punto.tipoDireccion || null,
  calleDesde: punto.calle || "",
  numeroDesde: punto.numero || "",
  pisoDesde: punto.piso || "",
  departamentoDesde: punto.departamento || "",
  entreCalleDesde: punto.entreCalle || "",
  entreCalleDesde1: punto.entreCalle1 || "",
  localidadDesde: punto.localidad || "Capital Federal",
  provinciaDesde: punto.provincia || "Capital Federal",
  codigoPostalDesde: punto.codigoPostal || "",
  telefonosDesde: punto.telefonos || "",
});

export const direccionValuesToPunto = (values) => ({
  tipoDireccion: values.tipoDireccion,
  fechaHora: values.fechaHora,
  observaciones: values.observaciones || "",
  calle: values.calleDesde || "",
  numero: values.numeroDesde || "",
  piso: values.pisoDesde || "",
  departamento: values.departamentoDesde || "",
  entreCalle: values.entreCalleDesde || "",
  entreCalle1: values.entreCalleDesde1 || "",
  localidad: values.localidadDesde || "",
  provincia: values.provinciaDesde || "",
  codigoPostal: values.codigoPostalDesde || "",
  telefonos: values.telefonosDesde || "",
});

export const locationFromCoords = (latitud, longitud) => {
  if (latitud === "" || latitud == null || longitud === "" || longitud == null) {
    return null;
  }
  const lat = Number(latitud);
  const lng = Number(longitud);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
};

export const buildPuntoAddressQuery = (punto = {}) => {
  const parts = [
    punto.calle,
    punto.numero,
    punto.localidad,
    punto.provincia,
    "Argentina",
  ]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean);
  return parts.length >= 2 ? parts.join(", ") : "";
};

export const buildPuntoMapSearches = (puntos = []) =>
  (puntos || []).map((punto, index) => ({
    key: punto.id || `punto-${index}`,
    location:
      punto.location || locationFromCoords(punto.latitud, punto.longitud),
    query: buildPuntoAddressQuery(punto),
    title: getPuntoHeaderTitle(index, puntos.length, punto),
  }));

export const getModalidadLabel = (options, idModalidad) => {
  if (idModalidad === "" || idModalidad == null) return "";
  const option = (options || []).find(
    (item) => String(item.id) === String(idModalidad),
  );
  return catalogOptionLabel(option).trim();
};

export const modalidadSoloRetorno = (options, idModalidad) => {
  const label = getModalidadLabel(options, idModalidad).toLowerCase();
  if (!label) return false;
  if (/^solo\s*retorno$/i.test(label)) return true;
  return /retorno|vuelta/.test(label) && !/ida/.test(label);
};

export const modalidadAgregaRetornoAutomatico = (options, idModalidad) => {
  const label = getModalidadLabel(options, idModalidad).toLowerCase();
  if (!label || modalidadSoloRetorno(options, idModalidad)) return false;
  if (/^ida$/i.test(label)) return false;
  return /ida/.test(label) && /retorno|vuelta/.test(label);
};

export const syncPuntosModalidadRetorno = (
  puntos,
  modalidades = [],
  idModalidad = "",
  tramosDefaults = {},
  idEstadoInicial = null,
) => {
  const current = ensureMinPuntos(puntos);
  if (modalidadSoloRetorno(modalidades, idModalidad)) {
    return current;
  }
  return applyModalidadRetorno(
    current,
    modalidadAgregaRetornoAutomatico(modalidades, idModalidad),
    tramosDefaults,
    idEstadoInicial,
  );
};

export const findRetornoIndex = (puntos = []) =>
  puntos.findIndex((punto, index) => index > 0 && punto.esRetorno);

export const insertPuntoBeforeRetorno = (puntos = [], newPunto) => {
  const retornoIndex = findRetornoIndex(puntos);
  if (retornoIndex < 0) return [...puntos, newPunto];
  return [
    ...puntos.slice(0, retornoIndex),
    newPunto,
    ...puntos.slice(retornoIndex),
  ];
};

const toOptionalDecimal = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toOptionalId = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveRetornoFechaHora = (puntos, retornoIndex, retorno = {}) =>
  toFormikDate(retorno.fechaHora) ??
  (retornoIndex > 0 ? toFormikDate(puntos[retornoIndex - 1]?.fechaHora) : null);

const puntoCoords = (punto = {}) => {
  const location =
    punto.location || locationFromCoords(punto.latitud, punto.longitud);
  return {
    latitud:
      punto.latitud === "" || punto.latitud == null
        ? location?.lat ?? ""
        : punto.latitud,
    longitud:
      punto.longitud === "" || punto.longitud == null
        ? location?.lng ?? ""
        : punto.longitud,
    location,
  };
};

export const copyPuntoAddress = (punto = {}) => {
  const coords = puntoCoords(punto);
  return {
    tipoDireccion: punto.tipoDireccion || "",
    calle: punto.calle || "",
    numero: punto.numero || "",
    piso: punto.piso || "",
    departamento: punto.departamento || "",
    entreCalle: punto.entreCalle || "",
    entreCalle1: punto.entreCalle1 || "",
    localidad: punto.localidad || "",
    provincia: punto.provincia || "",
    codigoPostal: punto.codigoPostal || "",
    telefonos: punto.telefonos || "",
    latitud: coords.latitud,
    longitud: coords.longitud,
    formattedAddress: punto.formattedAddress || "",
    placeId: punto.placeId || "",
    location: coords.location,
  };
};

const hasPuntoCoords = (punto = {}) => {
  const coords = puntoCoords(punto);
  return (
    toOptionalDecimal(coords.latitud) != null &&
    toOptionalDecimal(coords.longitud) != null
  );
};

const samePuntoCoords = (left, right) => {
  const leftCoords = puntoCoords(left);
  const rightCoords = puntoCoords(right);
  return (
    toOptionalDecimal(leftCoords.latitud) ===
    toOptionalDecimal(rightCoords.latitud) &&
    toOptionalDecimal(leftCoords.longitud) ===
    toOptionalDecimal(rightCoords.longitud)
  );
};

export const applyModalidadRetorno = (
  puntos,
  incluyeRetorno,
  tramosDefaults,
  idEstadoInicial = null,
) => {
  const current = ensureMinPuntos(puntos);

  const origin = current[0];

  if (!incluyeRetorno) {
    let next = current;
    while (
      next.length > 2 &&
      isAutoRetornoPunto(next[next.length - 1], origin)
    ) {
      const last = next[next.length - 1];
      if (isTramoRecorridoBloqueado(last)) {
        break;
      }
      next = next.slice(0, -1);
    }
    if (
      next.length >= 2 &&
      isAutoRetornoPunto(next[next.length - 1], origin)
    ) {
      const last = next[next.length - 1];
      if (!isTramoRecorridoBloqueado(last)) {
        next = [...next.slice(0, -1), { ...last, esRetorno: false }];
      }
    }
    return next;
  }
  const retornoIndex = findRetornoIndex(current);

  if (retornoIndex >= 0) {
    const retorno = current[retornoIndex];
    if (isTramoRecorridoBloqueado(retorno)) {
      return current;
    }
    const syncedRetorno = {
      ...retorno,
      ...copyPuntoAddress(origin),
      fechaHora: resolveRetornoFechaHora(current, retornoIndex, retorno),
      esRetorno: true,
    };
    if (
      retorno.esRetorno &&
      hasPuntoCoords(retorno) &&
      samePuntoAddress(retorno, syncedRetorno) &&
      samePuntoCoords(retorno, syncedRetorno) &&
      toFormikDate(retorno.fechaHora)?.getTime() ===
      toFormikDate(syncedRetorno.fechaHora)?.getTime()
    ) {
      return current;
    }
    const next = [...current];
    next[retornoIndex] = syncedRetorno;
    return next;
  }

  return [
    ...current,
    createEmptyPunto({
      ...copyPuntoAddress(origin),
      ...copyTramoGestion(tramosDefaults),
      fechaHora: resolveRetornoFechaHora(current, current.length),
      esRetorno: true,
    }),
  ];
};

export const getIdEstadoInicialTraslados = (fields) => {
  const options = getFormFieldOptions(
    fields,
    "idEstadoInicialTraslados",
    "IdEstadoInicialTraslados",
  );
  return options[0]?.id ?? null;
};

/** MLP-1820 flags vienen en cada tramo del backend (recorridoEditable / gestionEditable). */
export const isTramoRecorridoBloqueado = (destino) =>
  destino?.recorridoEditable === false;

/** MLP-1894 con tramos avanzados no se cambia modalidad ni recorrido (sí agencia en bandeja). */
export const isModalidadRecorridoLocked = (puntos = []) =>
  (puntos || []).some(
    (punto, index) => index > 0 && isTramoRecorridoBloqueado(punto),
  );

/**
 * Datos de gestión editables en el FPT (MLP-1820). Despachado/negativo/cerrado: no.
 * Pendiente autorización y rechazado por agencia: sí (agencia por tramo, MLP-1826 CA05).
 */
export const isTramoGestionEditable = (destino) =>
  destino?.gestionEditable !== false;

/** Alias usado por recorrido y “Aplicar a todos”. */
export const isTramoEditable = (destino) => isTramoGestionEditable(destino);

/**
 * El punto nuevo se inserta antes del retorno, así que reescribe el origen de
 * ese tramo. Si ya salió del estado inicial no se puede tocar.
 */
export const canAddTramo = (puntos = [], idEstadoInicial) => {
  const retornoIndex = findRetornoIndex(puntos);
  if (retornoIndex < 0) return true;
  return isTramoEditable(puntos[retornoIndex], idEstadoInicial);
};

export const isPuntoAddressLocked = (puntos, index, idEstadoInicial) => {
  if (!puntos?.length) return false;
  if (isPuntoRetornoAuto(puntos[index])) return true;
  if (index > 0 && !isTramoEditable(puntos[index], idEstadoInicial)) return true;
  if (
    index < puntos.length - 1 &&
    !isTramoEditable(puntos[index + 1], idEstadoInicial)
  ) {
    return true;
  }
  return false;
};

export const recorridoSoloLecturaPrestador = (esPrestador, puntos) =>
  Boolean(
    esPrestador &&
    (puntos || []).slice(1).some(isTramoRecorridoBloqueado),
  );

export const samePuntoAddress = (left, right) => {
  if (!left || !right) return false;
  return (
    String(left.calle || "").trim().toLowerCase() ===
    String(right.calle || "").trim().toLowerCase() &&
    String(left.numero || "").trim() === String(right.numero || "").trim() &&
    String(left.localidad || "").trim().toLowerCase() ===
    String(right.localidad || "").trim().toLowerCase() &&
    String(left.provincia || "").trim().toLowerCase() ===
    String(right.provincia || "").trim().toLowerCase()
  );
};

const isAutoRetornoPunto = (punto, origin) =>
  Boolean(punto?.esRetorno) && samePuntoAddress(punto, origin);

export const summarizePunto = (punto) => {
  const calleNumero = [punto.calle, punto.numero].filter(Boolean).join(" ");
  const codigoPostal = punto.codigoPostal ? `CP: ${punto.codigoPostal}` : "";
  return [calleNumero, codigoPostal, punto.localidad || punto.provincia]
    .filter(Boolean)
    .join(" - ");
};

export const buildPuntoResumenAddress = (punto = {}) => {
  const calleNumero = [punto.calle, punto.numero].filter(Boolean).join(" ");
  const pisoDepto = [punto.piso, punto.departamento].filter(Boolean).join(" ");
  const callePart = [calleNumero, pisoDepto ? `piso ${pisoDepto}` : ""]
    .filter(Boolean)
    .join(", ");
  const localidadPart = punto.localidad || punto.provincia || "";
  if (callePart && localidadPart) return `${callePart} – ${localidadPart}`;
  return callePart || localidadPart || summarizePunto(punto) || "—";
};

const parseDistanciaLabelToMeters = (label) => {
  if (!label) return null;
  const str = String(label).trim().toLowerCase();
  const numeric = Number(str.replace(/[^\d.,-]/g, "").replace(",", "."));
  if (!Number.isFinite(numeric)) return null;
  if (str.includes("km")) return numeric * 1000;
  if (/\d\s*m\s*$/.test(str) || /\d\s*m\b/.test(str)) return numeric;
  return numeric * 1000;
};

export const getPuntoDistanciaMeters = (punto = {}) => {
  if (punto.distanceMeters != null && punto.distanceMeters !== "") {
    const meters = Number(punto.distanceMeters);
    if (Number.isFinite(meters)) return meters;
  }
  if (punto.distanciaLabel) {
    return parseDistanciaLabelToMeters(punto.distanciaLabel);
  }
  return null;
};

export const getPuntoDistanciaKm = (punto = {}) => {
  const meters = getPuntoDistanciaMeters(punto);
  return meters == null ? null : meters / 1000;
};

/** Tramo destino con distancia ya persistida (p. ej. DistanciaKM del backend). */
export const hasPersistedSegmentDistance = (punto = {}) =>
  getPuntoDistanciaMeters(punto) != null;

/**
 * true si cada tramo con origen/destino geolocalizado ya trae distancia guardada.
 * coordsForPunto(punto) => { lat, lng } | null
 */
export const canUsePersistedRouteDistances = (puntos = [], coordsForPunto) => {
  if (!puntos?.length || puntos.length < 2 || typeof coordsForPunto !== "function") {
    return false;
  }
  let needsDistance = false;
  for (let index = 1; index < puntos.length; index += 1) {
    const origin = coordsForPunto(puntos[index - 1]);
    const destination = coordsForPunto(puntos[index]);
    if (!origin || !destination) continue;
    needsDistance = true;
    if (!hasPersistedSegmentDistance(puntos[index])) return false;
  }
  return needsDistance;
};

export const formatDistanciaResumen = (distanceMeters) => {
  if (distanceMeters == null || !Number.isFinite(Number(distanceMeters))) {
    return "—";
  }
  return formatDistance(Number(distanceMeters)) || "—";
};

export const buildTramosResumenRows = (puntos = []) => {
  const tramosCount = Math.max(puntos.length - 1, 0);
  if (tramosCount < 1) return [];

  return puntos.slice(1).map((destino, offset) => {
    const origen = puntos[offset];
    const tramoIndex = offset + 1;
    return {
      tramoIndex,
      origenLabel:
        getTipoDireccionNombre(origen?.tipoDireccion) ||
        getPuntoHeaderTitle(offset, puntos.length, origen),
      origenAddress: buildPuntoResumenAddress(origen),
      destinoLabel:
        getTipoDireccionNombre(destino?.tipoDireccion) ||
        getPuntoHeaderTitle(tramoIndex, puntos.length, destino),
      destinoAddress: buildPuntoResumenAddress(destino),
      fechaHora: destino?.fechaHora || null,
      esRetorno: Boolean(destino?.esRetorno),
      distanciaMeters: getPuntoDistanciaMeters(destino),
    };
  });
};

export const getTipoDireccionNombre = (tipoDireccion) =>
  TIPOS_DIRECCION.find((tipo) => String(tipo.id) === String(tipoDireccion))
    ?.nombre || "";

export const summarizePuntoConTipo = (punto = {}) =>
  [getTipoDireccionNombre(punto.tipoDireccion), summarizePunto(punto)]
    .filter(Boolean)
    .join(" ");

export const getTramoGestionTitle = (origen, destino, index, total) =>
  `Tramo ${index} de ${total} — ${summarizePuntoConTipo(origen)} → ${summarizePuntoConTipo(destino)}`;

export const requiredPuntoErrors = (
  punto,
  index = 0,
  showArtOnlyFields = false,
) => {
  const errors = {};
  if (punto?.idEstado === 7 || punto?.cancelado) return errors;
  const retornoAuto = isPuntoRetornoAuto(punto);
  if (!retornoAuto) {
    if (!punto.tipoDireccion) errors.tipoDireccion = "Campo requerido";
    if (!punto.provincia) errors.provincia = "Campo requerido";
    if (!punto.localidad) errors.localidad = "Campo requerido";
    if (!punto.calle) errors.calle = "Campo requerido";
    if (!punto.numero) errors.numero = "Campo requerido";
    if (!punto.codigoPostal) errors.codigoPostal = "Campo requerido";
  }
  if (index > 0 && !punto.fechaHora) errors.fechaHora = "Campo requerido";
  if (index > 0) {
    if (!punto.idTipoTransporte || punto.idTipoTransporte === "0" || punto.idTipoTransporte === 0) {
      errors.idTipoTransporte = "Campo requerido";
    }
    if (!punto.idMotivoTraslado || punto.idMotivoTraslado === "0" || punto.idMotivoTraslado === 0) {
      errors.idMotivoTraslado = "Debe seleccionar un motivo de traslado válido";
    }
    if (showArtOnlyFields && punto.idTramo > 0) {
      if (!punto.idAgencia || punto.idAgencia === "0" || punto.idAgencia === 0) {
        errors.idAgencia = "Debe asignar una agencia válida";
      }
    }
  }
  return errors;
};

export const mergePuntoFechaErrors = (
  punto,
  index,
  puntos,
  fechaHoraTurno,
) => {
  const errors = {};
  if (index === 0) return errors;

  const fecha = toFormikDate(punto?.fechaHora);
  const minTurno = toFormikDate(fechaHoraTurno);

  if (fecha && minTurno && fecha.getTime() < minTurno.getTime()) {
    errors.fechaHora =
      'La fecha del tramo debe ser igual o posterior a la fecha "Para el".';
  }

  if (index > 0 && fecha) {
    const prevFecha = toFormikDate(puntos[index - 1]?.fechaHora);
    if (prevFecha && fecha.getTime() <= prevFecha.getTime()) {
      errors.fechaHora =
        "La fecha del tramo debe ser posterior a la del punto anterior.";
    }
  }

  return errors;
};

export const resolvePuntoMinDate = (fechaHoraTurno, prevFechaHora, index) => {
  const turno = toFormikDate(fechaHoraTurno);
  const prev = index > 0 ? toFormikDate(prevFechaHora) : null;
  if (prev && turno) {
    return prev.getTime() >= turno.getTime() ? prev : turno;
  }
  return prev || turno || undefined;
};

export const validatePuntosForm = (
  puntos,
  fechaHoraTurno,
  showArtOnlyFields = false,
) => {
  const recorridoError = validateRecorrido(puntos);
  if (recorridoError) return { puntos: recorridoError };

  const puntosErrors = (puntos || []).map((punto, index) => ({
    ...requiredPuntoErrors(punto, index, showArtOnlyFields),
    ...mergePuntoFechaErrors(punto, index, puntos, fechaHoraTurno),
  }));
  if (puntosErrors.some((errors) => Object.keys(errors).length > 0)) {
    return { puntos: puntosErrors };
  }

  return {};
};

export const movePunto = (puntos, from, to) => {
  if (to < 0 || to >= puntos.length) return puntos;
  if (isPuntoRetornoAuto(puntos[from]) || isPuntoRetornoAuto(puntos[to])) {
    return puntos;
  }
  const next = [...puntos];
  const [punto] = next.splice(from, 1);
  next.splice(to, 0, punto);
  if (from === 0 || to === 0) {
    const oldFirstTramo = puntos[1] || {};
    next[1] = {
      ...next[1],
      ...copyTramoGestion(oldFirstTramo),
      fechaHora: next[1].fechaHora ?? oldFirstTramo.fechaHora ?? null,
      idTramo: oldFirstTramo.idTramo || 0,
      nroTramoUnico: oldFirstTramo.nroTramoUnico ?? null,
      idEstado: oldFirstTramo.idEstado ?? null,
      idPrestadorTraslado: oldFirstTramo.idPrestadorTraslado || "",
    };
    next[0] = {
      ...next[0],
      fechaHora: null,
      idTramo: 0,
      nroTramoUnico: null,
      idEstado: null,
      distanceMeters: 0,
      distanciaLabel: "0 km",
    };
  }
  return next;
};

const legacyIdaDestino = (puntos = []) => {
  const retornoIndex = findRetornoIndex(puntos);
  if (retornoIndex > 1) return puntos[retornoIndex - 1];
  return puntos[puntos.length - 1];
};

const applyLegacyPoint = (target, punto, suffix) => {
  if (!punto) return target;
  return {
    ...target,
    [`calle${suffix}`]: punto.calle,
    [`numero${suffix}`]: punto.numero,
    [`piso${suffix}`]: punto.piso,
    [`departamento${suffix}`]: punto.departamento,
    [`entreCalle${suffix}`]: punto.entreCalle,
    [`entreCalle${suffix === "Desde" ? "Desde1" : "Hasta1"}`]:
      punto.entreCalle1,
    [`localidad${suffix}`]: punto.localidad,
    [`provincia${suffix}`]: punto.provincia,
    [`codigoPostal${suffix}`]: punto.codigoPostal,
    [`telefonos${suffix}`]: punto.telefonos,
    [`tipoDireccion${suffix}`]: punto.tipoDireccion || null,
  };
};

const formatDistanciaLabel = (distanciaKm) => {
  const km = Number(distanciaKm);
  if (!Number.isFinite(km)) return "";
  return formatDistance(km * 1000);
};

const tramoField = (tramo, ...keys) => {
  for (const key of keys) {
    const value = tramo?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const normalizeTramoFromApi = (tramo = {}) => ({
  idTramo: tramoField(tramo, "idTramo", "IdTramo") ?? 0,
  nroOrden: tramoField(tramo, "nroOrden", "NroOrden") ?? 0,
  nroTramoUnico: tramoField(tramo, "nroTramoUnico", "NroTramoUnico") ?? null,
  idEstado: tramoField(tramo, "idEstado", "IdEstado") ?? null,
  recorridoEditable:
    tramoField(tramo, "recorridoEditable", "RecorridoEditable") ?? true,
  gestionEditable:
    tramoField(tramo, "gestionEditable", "GestionEditable") ?? true,
  fechaHoraTraslado: tramoField(tramo, "fechaHoraTraslado", "FechaHoraTraslado"),
  esRetorno: Boolean(tramoField(tramo, "esRetorno", "EsRetorno")),
  distanciaKM: tramoField(tramo, "distanciaKM", "DistanciaKM"),
  calleOrigen: tramoField(tramo, "calleOrigen", "CalleOrigen") ?? "",
  numeroOrigen: tramoField(tramo, "numeroOrigen", "NumeroOrigen") ?? "",
  pisoOrigen: tramoField(tramo, "pisoOrigen", "PisoOrigen") ?? "",
  deptoOrigen: tramoField(tramo, "deptoOrigen", "DeptoOrigen") ?? "",
  cpOrigen: tramoField(tramo, "cpOrigen", "CPOrigen") ?? "",
  entreCallesOrigen: tramoField(tramo, "entreCallesOrigen", "EntreCallesOrigen") ?? "",
  localidadOrigen: tramoField(tramo, "localidadOrigen", "LocalidadOrigen") ?? "",
  provinciaOrigen: tramoField(tramo, "provinciaOrigen", "ProvinciaOrigen") ?? "",
  idTipoDireccionOrigen:
    tramoField(tramo, "idTipoDireccionOrigen", "IdTipoDireccionOrigen") ?? "",
  telefonoOrigen: tramoField(tramo, "telefonoOrigen", "TelefonoOrigen") ?? "",
  latOrigen: tramoField(tramo, "latOrigen", "LatOrigen"),
  lngOrigen: tramoField(tramo, "lngOrigen", "LngOrigen"),
  calleDestino: tramoField(tramo, "calleDestino", "CalleDestino") ?? "",
  numeroDestino: tramoField(tramo, "numeroDestino", "NumeroDestino") ?? "",
  pisoDestino: tramoField(tramo, "pisoDestino", "PisoDestino") ?? "",
  deptoDestino: tramoField(tramo, "deptoDestino", "DeptoDestino") ?? "",
  cpDestino: tramoField(tramo, "cpDestino", "CPDestino") ?? "",
  entreCallesDestino:
    tramoField(tramo, "entreCallesDestino", "EntreCallesDestino") ?? "",
  localidadDestino: tramoField(tramo, "localidadDestino", "LocalidadDestino") ?? "",
  provinciaDestino: tramoField(tramo, "provinciaDestino", "ProvinciaDestino") ?? "",
  idTipoDireccionDestino:
    tramoField(tramo, "idTipoDireccionDestino", "IdTipoDireccionDestino") ?? "",
  telefonoDestino: tramoField(tramo, "telefonoDestino", "TelefonoDestino") ?? "",
  latDestino: tramoField(tramo, "latDestino", "LatDestino"),
  lngDestino: tramoField(tramo, "lngDestino", "LngDestino"),
  observTramo: tramoField(tramo, "observTramo", "ObservTramo") ?? "",
  idTipoTransporte:
    tramoField(tramo, "idTipoTransporte", "IdTipoTransporte") ?? "",
  idPrestadorTraslado:
    tramoField(tramo, "idPrestadorTraslado", "IdPrestadorTraslado") ?? "",
  idAgencia: tramoField(tramo, "idAgencia", "IdAgencia") ?? 0,
  idMotivoTraslado: tramoField(tramo, "idMotivoTraslado", "IdMotivoTraslado") ?? 0,
  pool: Boolean(tramoField(tramo, "pool", "Pool")),
  acompañante: Boolean(tramoField(tramo, "acompañante", "Acompañante", "acompanante", "Acompanante")),
  sillaRuedas: Boolean(tramoField(tramo, "sillaRuedas", "SillaRuedas")),
  muletas: Boolean(tramoField(tramo, "muletas", "Muletas")),
});

export const resolveTrasladoTramos = (values = {}) =>
  coerceToArray(values.trasladoTramos ?? values.TrasladoTramos).map(
    normalizeTramoFromApi,
  );

const puntoFromTramoSide = (tramo, side) => {
  const isOrigen = side === "origen";
  const latitud = isOrigen ? tramo.latOrigen : tramo.latDestino;
  const longitud = isOrigen ? tramo.lngOrigen : tramo.lngDestino;
  const distanciaKm = isOrigen ? 0 : tramo.distanciaKM;
  return createEmptyPunto({
    idTramo: isOrigen ? undefined : tramo.idTramo,
    idEstado: isOrigen ? undefined : tramo.idEstado,
    nroTramoUnico: isOrigen ? undefined : tramo.nroTramoUnico,
    tipoDireccion: isOrigen
      ? tramo.idTipoDireccionOrigen
      : tramo.idTipoDireccionDestino,
    fechaHora: isOrigen ? null : tramo.fechaHoraTraslado,
    calle: isOrigen ? tramo.calleOrigen : tramo.calleDestino,
    numero: isOrigen ? tramo.numeroOrigen : tramo.numeroDestino,
    piso: isOrigen ? tramo.pisoOrigen : tramo.pisoDestino,
    departamento: isOrigen ? tramo.deptoOrigen : tramo.deptoDestino,
    entreCalle: isOrigen ? tramo.entreCallesOrigen : tramo.entreCallesDestino,
    localidad: isOrigen ? tramo.localidadOrigen : tramo.localidadDestino,
    provincia: isOrigen ? tramo.provinciaOrigen : tramo.provinciaDestino,
    codigoPostal: isOrigen ? tramo.cpOrigen : tramo.cpDestino,
    telefonos: isOrigen ? tramo.telefonoOrigen : tramo.telefonoDestino,
    latitud: latitud ?? "",
    longitud: longitud ?? "",
    location: locationFromCoords(latitud, longitud),
    distanceMeters:
      distanciaKm == null || distanciaKm === ""
        ? isOrigen
          ? 0
          : null
        : Number(distanciaKm) * 1000,
    distanciaLabel: isOrigen ? "0 km" : formatDistanciaLabel(distanciaKm),
    esRetorno: !isOrigen && Boolean(tramo.esRetorno),
    ...(isOrigen
      ? {}
      : {
        ...copyTramoGestion({
          idTipoTransporte: tramo.idTipoTransporte ?? "",
          idMotivoTraslado: tramo.idMotivoTraslado ?? 0,
          idPrestadorTraslado: tramo.idPrestadorTraslado ?? "",
          idAgencia: tramo.idAgencia ?? 0,
          pool: Boolean(tramo.pool),
          acompanante: Boolean(tramo.acompañante),
          sillaRuedas: Boolean(tramo.sillaRuedas),
          muletas: Boolean(tramo.muletas),
          observacionesTramo: tramo.observTramo || "",
        }),
        idTramo: tramo.idTramo || 0,
        nroTramoUnico: tramo.nroTramoUnico ?? null,
        idEstado: tramo.idEstado ?? null,
        idPrestadorTraslado: tramo.idPrestadorTraslado || "",
        recorridoEditable: tramo.recorridoEditable,
        gestionEditable: tramo.gestionEditable,
      }),
  });
};

export const tramosToPuntos = (tramos = []) => {
  if (!tramos.length) return [];
  const ordered = [...tramos]
    .map(normalizeTramoFromApi)
    .sort((left, right) => (left.nroOrden ?? 0) - (right.nroOrden ?? 0));
  return [
    puntoFromTramoSide(ordered[0], "origen"),
    ...ordered.map((tramo) => puntoFromTramoSide(tramo, "destino")),
  ];
};

export const fptHasTraslados = (form) =>
  Boolean(
    form?.puntos?.length ||
    form?.tramos?.length ||
    resolveTrasladoTramos(form).length,
  );

export const prepareFptTrasladosValues = (
  values,
  modalidades = [],
  idEstadoInicial = null,
) => {
  const tramos = resolveTrasladoTramos(values);
  const firstTramo = tramos[0] || {};
  const rawTipoTransporte =
    values.tramosDefaults?.idTipoTransporte ??
    values.idTipoTransporte ??
    values.IdTipoTransporte ??
    firstTramo.idTipoTransporte ??
    values.tipoTransporteNombre ??
    "";
  const rawMotivoTraslado =
    values.tramosDefaults?.idMotivoTraslado ??
    values.idMotivoTraslado ??
    values.IdMotivoTraslado ??
    firstTramo.idMotivoTraslado ??
    0;
  const rawPrestadorTraslado =
    values.tramosDefaults?.idPrestadorTraslado ??
    firstTramo.idPrestadorTraslado ??
    "";
  const rawAgencia =
    values.tramosDefaults?.idAgencia ??
    values.idAgencia ??
    values.IdAgencia ??
    firstTramo.idAgencia ??
    0;
  const tramosDefaults = normalizeTramoGestion({
    ...values.tramosDefaults,
    idTipoTransporte: rawTipoTransporte,
    idMotivoTraslado: rawMotivoTraslado,
    idPrestadorTraslado: rawPrestadorTraslado,
    idAgencia: rawAgencia,
  });
  const idModalidad = emptySelectValue(
    resolveFormValue(values, "idModalidad", "IdModalidad"),
  );
  const puntosBase = ensureMinPuntos(
    tramos.length
      ? tramosToPuntos(tramos)
      : values.puntos?.length
        ? values.puntos
        : [],
  ).map((punto, index) => {
    const normalized = normalizePunto(punto);
    if (index === 0) return { ...normalized, fechaHora: null };
    return {
      ...normalized,
      ...normalizeTramoGestion({
        ...tramosDefaults,
        ...punto,
        idTipoTransporte: punto.idTipoTransporte || rawTipoTransporte || "",
        idMotivoTraslado:
          punto.idMotivoTraslado !== "" && punto.idMotivoTraslado != null
            ? punto.idMotivoTraslado
            : rawMotivoTraslado,
        idPrestadorTraslado:
          punto.idPrestadorTraslado || rawPrestadorTraslado || "",
        idAgencia:
          punto.idAgencia !== "" && punto.idAgencia != null
            ? punto.idAgencia
            : rawAgencia,
        observacionesTramo:
          punto.observacionesTramo || punto.observaciones || "",
      }),
    };
  });

  return {
    ...values,
    fechaHoraSolicitud: resolveFormDate(
      values,
      "fechaHoraSolicitud",
      "FechaHoraSolicitud",
    ),
    fechaHoraTurno: resolveFormDate(values, "fechaHoraTurno", "FechaHoraTurno"),
    solicitadoPor: toFormikString(values.solicitadoPor),
    observaciones: toFormikString(values.observaciones),
    tipoTransporteNombre: emptySelectValue(values.tipoTransporteNombre),
    tipoUrgenciaNombre: emptySelectValue(values.tipoUrgenciaNombre),
    esperaRetorno:
      resolveFormValue(values, "esperaRetorno", "EsperaRetorno") || "No",
    origenPedido: toFormikString(
      resolveFormValue(values, "origenPedido", "OrigenPedido"),
    ),
    idModalidad,
    idTipoTraslado: emptySelectValue(
      resolveFormValue(values, "idTipoTraslado", "IdTipoTraslado"),
    ),
    idTipoSolicitante: emptySelectValue(
      resolveFormValue(values, "idTipoSolicitante", "IdTipoSolicitante"),
    ),
    tramosDefaults,
    trasladoTramos: tramos,
    puntos:
      tramos.length > 0
        ? puntosBase
        : syncPuntosModalidadRetorno(
          puntosBase,
          modalidades,
          idModalidad,
          tramosDefaults,
          idEstadoInicial,
        ),
  };
};

export const syncLegacyPuntosToForm = (puntos, setFieldValue) => {
  const first = puntos[0];
  const hasta = legacyIdaDestino(puntos);
  const synced = applyLegacyPoint(
    applyLegacyPoint({}, first, "Desde"),
    hasta,
    "Hasta",
  );
  Object.entries(synced).forEach(([field, value]) => {
    setFieldValue(field, value);
  });
};

const puntoAddressSide = (punto, side) => {
  const isOrigen = side === "origen";
  const prefix = isOrigen ? "Origen" : "Destino";
  const coords = puntoCoords(punto);
  return {
    [`calle${prefix}`]: punto.calle || "",
    [`numero${prefix}`]: punto.numero != null ? String(punto.numero) : "",
    [`piso${prefix}`]: punto.piso || "",
    [`depto${prefix}`]: punto.departamento || "",
    [`cp${prefix}`]: punto.codigoPostal != null ? String(punto.codigoPostal) : "",
    [`entreCalles${prefix}`]:
      [punto.entreCalle, punto.entreCalle1].filter(Boolean).join(" ") || "",
    [`localidad${prefix}`]: punto.localidad || "",
    [`provincia${prefix}`]: punto.provincia || "",
    [`idTipoDireccion${prefix}`]: toOptionalId(punto.tipoDireccion),
    [`telefono${prefix}`]: punto.telefonos || "",
    [`lat${prefix}`]: toOptionalDecimal(coords.latitud),
    [`lng${prefix}`]: toOptionalDecimal(coords.longitud),
  };
};

export const puntosToTramos = (puntos = []) => {
  if (puntos.length < 2) return [];
  return puntos.slice(1).map((destino, index) => {
    const origen = puntos[index];
    const distanciaKm =
      destino.distanceMeters == null || destino.distanceMeters === ""
        ? null
        : Number(destino.distanceMeters) / 1000;
    return {
      idTramo: destino.idTramo || 0,
      nroTramoUnico: destino.nroTramoUnico || null,
      idEstado: destino.idEstado || null,
      nroOrden: index + 1,
      fechaHoraTraslado: destino.fechaHora,
      esRetorno: Boolean(destino.esRetorno),
      distanciaKM: Number.isFinite(distanciaKm)
        ? distanciaKm
        : destino.esRetorno
          ? 0
          : null,
      ...puntoAddressSide(origen, "origen"),
      ...puntoAddressSide(destino, "destino"),
      observTramo: destino.observacionesTramo || "",
      idTipoTransporte: toOptionalId(destino.idTipoTransporte),
      idPrestadorTraslado: toOptionalId(destino.idPrestadorTraslado),
      idAgencia: toOptionalId(destino.idAgencia) ?? 0,
      idMotivoTraslado: toOptionalId(destino.idMotivoTraslado) ?? 0,
      pool: Boolean(destino.pool),
      acompañante: Boolean(destino.acompanante),
      sillaRuedas: Boolean(destino.sillaRuedas),
      muletas: Boolean(destino.muletas),
    };
  });
};

const normalizeTramoForSubmit = (tramo) => ({
  ...tramo,
  fechaHoraTraslado: convertDateTimeToNet(tramo.fechaHoraTraslado),
  distanciaKM: toOptionalDecimal(tramo.distanciaKM) ?? 0,
  latOrigen: toOptionalDecimal(tramo.latOrigen),
  lngOrigen: toOptionalDecimal(tramo.lngOrigen),
  latDestino: toOptionalDecimal(tramo.latDestino),
  lngDestino: toOptionalDecimal(tramo.lngDestino),
  idTipoDireccionOrigen: toOptionalId(tramo.idTipoDireccionOrigen),
  idTipoDireccionDestino: toOptionalId(tramo.idTipoDireccionDestino),
  idTipoTransporte: toOptionalId(tramo.idTipoTransporte),
  idAgencia: toOptionalId(tramo.idAgencia) ?? 0,
  idMotivoTraslado: toOptionalId(tramo.idMotivoTraslado) ?? 0,
});

export const transformFptTrasladosSubmitValues = (
  values,
  modalidades = [],
  idEstadoInicial = null,
) => {
  const { tramosDefaults, puntos: rawPuntos, ...rest } = values;
  const idModalidad = emptySelectValue(
    resolveFormValue(rest, "idModalidad", "IdModalidad"),
  );
  const puntos = syncPuntosModalidadRetorno(
    rawPuntos || [],
    modalidades,
    idModalidad,
    tramosDefaults,
    idEstadoInicial,
  );
  const first = puntos[0];
  const hasta = legacyIdaDestino(puntos);
  const trasladoTramos = puntosToTramos(puntos).map(normalizeTramoForSubmit);
  const tipoTransporteNombre =
    rest.tipoTransporteNombre ||
    trasladoTramos.find((tramo) => tramo.idTipoTransporte)?.idTipoTransporte ||
    tramosDefaults?.idTipoTransporte ||
    "";
  let submitValues = {
    ...rest,
    tipoTransporteNombre,
    trasladoTramos,
    esperaRetorno: puntos.some((punto) => punto.esRetorno) ? "Sí" : "No",
    idModalidad: toOptionalId(rest.idModalidad),
    idTipoTraslado: toOptionalId(rest.idTipoTraslado),
    idTipoSolicitante: toOptionalId(rest.idTipoSolicitante),
  };
  submitValues = applyLegacyPoint(submitValues, first, "Desde");
  submitValues = applyLegacyPoint(submitValues, hasta, "Hasta");
  return submitValues;
};

export const validateRecorrido = (puntos) => {
  if (!puntos || puntos.length < 2) {
    return "Debe cargar al menos dos puntos en el recorrido.";
  }

  for (let i = 0; i < puntos.length - 1; i++) {
    const current = puntos[i];
    const next = puntos[i + 1];
    const currentHasAddress = Boolean(
      (current.calle || "").trim() || (current.localidad || "").trim()
    );
    if (currentHasAddress && samePuntoAddress(current, next)) {
      return "No debe haber 2 puntos consecutivos con la misma dirección.";
    }
  }

  return undefined;
};
